import {
    diaryEntity,
    drawingSheetEntity,
    findEntity,
    findingEntity,
    findingPlanumEntity,
    findingProfileEntity,
    groupEntity,
    mediaEntity,
    mediaLinkEntity,
    mediaGroupEntity,
    overallDescriptionEntity,
    areaDescriptionEntity,
    sampleEntity,
    parcelEntity,
    photoEntity,
    projectEntity,
    projectGroupEntity,
    timeEntryEntity,
    type Action,
    type Subject
} from '@ast-data/shared';
import { WORKER_TIME_ENTRY_VARIANT } from '../timeTracking/timeEntryVariant';
import { EDIT_PARAM } from '../admin/useEditParam';

/**
 * Where the record behind a change-log row lives, so the log can link to it.
 *
 * Only the office routes: the change log is an office screen (`/office/admin/
 * history`), so there is no field variant to branch on the way `useFindingBase
 * Path` has to.
 *
 * Not every kind of record has a screen of its own. A Benutzerprofil is edited
 * inside a list, so there is no address to send anyone to; such entity types
 * are simply absent from the table below and the log shows no link for them. A
 * Team, a Projekt-Gruppe and a Flurstück are edited in a dialog on their list,
 * which `?edit=<id>` opens (see useEditParam). A Medium lives inside its
 * description's dialog, so the log links to that description instead (see
 * OWNED_ROUTES).
 */

/** The little a route needs from the record as the app has it right now. */
export interface HistoryRecordDoc {
    id: string;
    /** Absent on the project-less entities (Zeiterfassung). */
    projectId?: string;
    /** The Befund a Planum/Profil hangs under. */
    findingId?: string;
    /** The record a Medium belongs to. */
    ownerType?: string;
    ownerId?: string;
}

interface RecordRoute {
    /** RxDB collection the record is read from. */
    collection: string;
    /** The office route, or null when this record cannot be addressed. */
    path: (doc: HistoryRecordDoc) => string | null;
    /**
     * What the route's screen demands, where that is narrower than reading the
     * change log: a link the reader may not follow would end on a refusal.
     */
    requires?: [Action, Subject];
}

/** A route under the record's own project, or null when it has no project. */
function inProject(doc: HistoryRecordDoc, tail: string): string | null {
    if (!doc.projectId) {
        return null;
    }
    return `/office/projects/${doc.projectId}/${tail}`;
}

const ROUTES: Record<string, RecordRoute> = {
    [projectEntity.name]: {
        collection: projectEntity.collection,
        // The Maßnahme's own screen is its Stammdaten, not the Befund list the
        // project route redirects to.
        path: (doc) => `/office/projects/${doc.id}/metadata`,
        requires: ['access', 'ProjectMetadata']
    },
    [parcelEntity.name]: {
        collection: parcelEntity.collection,
        // The dialog edits the parcel's whole area; the list finds it by the
        // one parcel the log names.
        path: (doc) => inProject(doc, `parcels?${EDIT_PARAM}=${doc.id}`)
    },
    [findingEntity.name]: {
        collection: findingEntity.collection,
        path: (doc) => inProject(doc, `findings/${doc.id}/edit`)
    },
    [findingPlanumEntity.name]: {
        collection: findingPlanumEntity.collection,
        /**
         * A description is addressed THROUGH its Befund, so without the link to
         * one there is no route — which is also why the record has to be read
         * rather than built from the log row, which carries no findingId.
         */
        path: (doc) =>
            doc.findingId
                ? inProject(
                      doc,
                      `findings/${doc.findingId}/planums/${doc.id}/edit`
                  )
                : null
    },
    [findingProfileEntity.name]: {
        collection: findingProfileEntity.collection,
        path: (doc) =>
            doc.findingId
                ? inProject(
                      doc,
                      `findings/${doc.findingId}/profiles/${doc.id}/edit`
                  )
                : null
    },
    [overallDescriptionEntity.name]: {
        collection: overallDescriptionEntity.collection,
        path: (doc) => inProject(doc, `overall-descriptions/${doc.id}/edit`)
    },
    [areaDescriptionEntity.name]: {
        collection: areaDescriptionEntity.collection,
        path: (doc) => inProject(doc, `area-descriptions/${doc.id}/edit`)
    },
    [findEntity.name]: {
        collection: findEntity.collection,
        path: (doc) => inProject(doc, `finds/${doc.id}/edit`)
    },
    [sampleEntity.name]: {
        collection: sampleEntity.collection,
        path: (doc) => inProject(doc, `samples/${doc.id}/edit`)
    },
    [photoEntity.name]: {
        collection: photoEntity.collection,
        path: (doc) => inProject(doc, `photos/${doc.id}/edit`)
    },
    [drawingSheetEntity.name]: {
        collection: drawingSheetEntity.collection,
        path: (doc) => inProject(doc, `drawing-sheets/${doc.id}/edit`)
    },
    [diaryEntity.name]: {
        collection: diaryEntity.collection,
        path: (doc) => inProject(doc, `diaries/${doc.id}/edit`)
    },
    [timeEntryEntity.name]: {
        collection: timeEntryEntity.collection,
        /**
         * Both Zeiterfassungen share one collection, and the Mitarbeiter route
         * forwards a Gerät to its own list by itself (see TimeEntryDialogRoute),
         * so the entry type does not have to be read here.
         */
        path: (doc) => `${WORKER_TIME_ENTRY_VARIANT.officePath}/${doc.id}/edit`,
        requires: ['access', 'WorkerTimeTracking']
    },
    [groupEntity.name]: {
        collection: groupEntity.collection,
        path: (doc) => `/office/admin/groups?${EDIT_PARAM}=${doc.id}`,
        requires: ['manage', 'Group']
    },
    [projectGroupEntity.name]: {
        collection: projectGroupEntity.collection,
        path: (doc) => `/office/admin/project-groups?${EDIT_PARAM}=${doc.id}`,
        requires: ['manage', 'ProjectGroup']
    }
};

/**
 * The collection a record of this entity type is read from, or null when the
 * change log has no screen to link to for it.
 */
export function historyRecordCollection(entityType: string): string | null {
    return ROUTES[entityType]?.collection ?? null;
}

/**
 * The right the linked screen asks for beyond the change log's own, or null
 * when reading the log is enough.
 */
export function historyRecordRequires(
    entityType: string
): [Action, Subject] | null {
    return ROUTES[entityType]?.requires ?? null;
}

/** The office route to this record, or null when it cannot be addressed. */
export function historyRecordPath(
    entityType: string,
    doc: HistoryRecordDoc
): string | null {
    return ROUTES[entityType]?.path(doc) ?? null;
}

/** The record another one lives inside, by entity type and id. */
export interface HistoryRecordOwner {
    entityType: string;
    id: string;
}

interface OwnedRoute {
    /** RxDB collection the owned record is read from. */
    collection: string;
    /** Its owner, or null when that owner has no screen to open. */
    owner: (doc: HistoryRecordDoc) => HistoryRecordOwner | null;
    /** The FormSection anchor the owner's dialog opens scrolled to. */
    anchor: string;
}

/**
 * Only the descriptions, the Probe and the Tagebuch: the owners whose dialog
 * shows its media.
 * A Befund could own a Medium by the schema, but its dialog has no section for
 * one, and a link there would promise photos the reader then cannot find.
 */
const MEDIA_OWNER_TYPES = new Set<string>([
    findingPlanumEntity.name,
    findingProfileEntity.name,
    overallDescriptionEntity.name,
    areaDescriptionEntity.name,
    sampleEntity.name,
    diaryEntity.name
]);

/** Records with no screen of their own, linked through the one they live in. */
const OWNED_ROUTES: Record<string, OwnedRoute> = {
    [mediaEntity.name]: {
        collection: mediaEntity.collection,
        owner: (doc) =>
            doc.ownerType && doc.ownerId && MEDIA_OWNER_TYPES.has(doc.ownerType)
                ? { entityType: doc.ownerType, id: doc.ownerId }
                : null,
        anchor: 'media'
    },
    // A linked picture shows in the description that links it.
    [mediaLinkEntity.name]: {
        collection: mediaLinkEntity.collection,
        owner: (doc) =>
            doc.ownerType && doc.ownerId && MEDIA_OWNER_TYPES.has(doc.ownerType)
                ? { entityType: doc.ownerType, id: doc.ownerId }
                : null,
        anchor: 'media'
    },
    // A picture group shows in the description it arranges.
    [mediaGroupEntity.name]: {
        collection: mediaGroupEntity.collection,
        owner: (doc) =>
            doc.ownerType && doc.ownerId && MEDIA_OWNER_TYPES.has(doc.ownerType)
                ? { entityType: doc.ownerType, id: doc.ownerId }
                : null,
        anchor: 'media'
    }
};

/**
 * How the log reaches a record that lives inside another one, or null when
 * this entity type has a screen of its own or none at all.
 */
export function historyOwnedRoute(entityType: string): OwnedRoute | null {
    return OWNED_ROUTES[entityType] ?? null;
}
